import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import SignatureCanvas from 'react-signature-canvas';
import { useSelector } from 'react-redux';
import { server } from "../../server";


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const MembershipCheckout = ({ prevStep, formData, handleChange }) => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [priceDetails, setPriceDetails] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [sessionId, setSessionId] = useState('');
    const sigCanvas = useRef({});

    const accountNameParts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
    const resolvedFirstName = user ? (accountNameParts[0] || formData.firstName) : formData.firstName;
    const resolvedLastName = user ? (accountNameParts.slice(1).join(' ') || formData.lastName) : formData.lastName;
    const resolvedEmail = user?.email || formData.email;
    const accountUniqueId = user?.uniqueId || user?.membership?.uniqueId;
    const resolvedQrCodeUrl = user?.qrCodeUrl || user?.membership?.qrCodeUrl;
    const canStartCheckout = Boolean(isAuthenticated && user?._id);

    useEffect(() => {
        const fetchPriceDetails = async () => {
            try {
                const response = await axios.get(`${server}/membership/memberships/price-details`);
                setPriceDetails(response.data);
            } catch (error) {
                console.error('Failed to fetch price details', error);
                toast.error('Failed to load pricing information.');
            }
        };

        fetchPriceDetails();
    }, []);

    const handlePaymentSubmission = async (event) => {
        event.preventDefault();

        if (!canStartCheckout) {
            toast.error('Sign in to the account you want to upgrade before starting membership checkout.');
            navigate('/login');
            return;
        }

        if (!resolvedEmail) {
            toast.error('Your account email is required before membership checkout can begin.');
            return;
        }

        if (formData.email && formData.email.trim().toLowerCase() !== resolvedEmail.trim().toLowerCase()) {
            toast.error('Membership checkout must use the email on your signed-in account.');
            return;
        }

        if (sigCanvas.current.isEmpty()) {
            toast.error('Please provide your digital signature.');
            return;
        }

        setIsLoading(true);
        const signatureImage = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        handleChange('signature1')(signatureImage);

        try {
            const { data } = await axios.post(`${server}/membership/create-membership-checkout-session`, {
                firstName: resolvedFirstName,
                lastName: resolvedLastName,
                email: resolvedEmail,
                dob: formData.dob,
                photo: formData.photo,
                priceId: priceDetails.id,
                signature1: signatureImage,
            }, {
                headers: {
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            });

            setClientSecret(data.clientSecret);
            setSessionId(data.sessionId);
        } catch (error) {
            console.error('Checkout session creation failed:', error);
            toast.error('Failed to create checkout session. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const clearSignature = () => {
        sigCanvas.current.clear();
        handleChange('signature1')('');
    };

    return (
        <div className="membership-checkout-container">
            <div className="membership-checkout-grid">
                <div className="membership-checkout-info">
                    {priceDetails && (
                        <div className="membership-checkout-details">
                            <h2>{`Level up with your new: ${priceDetails.product.name}`}</h2>
                            <p>{priceDetails.product.description}</p>
                            <p>Price: ${(priceDetails.unit_amount / 100).toFixed(2)} {priceDetails.currency.toUpperCase()}</p>
                        </div>
                    )}
                    <div className="membership-checkout-account-panel">
                        <p className="membership-checkout-account-eyebrow">Account Lane</p>
                        {canStartCheckout ? (
                            <>
                                <h3 className="membership-checkout-account-title">Membership will attach to your existing BuzzVibe account.</h3>
                                <p className="membership-checkout-account-copy">
                                    Stripe checkout will use <strong>{resolvedEmail}</strong> and keep your original unique ID and QR in sync with the membership lane.
                                </p>
                                {accountUniqueId ? (
                                    <p className="membership-checkout-account-copy">
                                        <strong>Unique ID:</strong> {accountUniqueId}
                                    </p>
                                ) : null}
                                {resolvedQrCodeUrl ? (
                                    <img src={resolvedQrCodeUrl} alt="Your original BuzzVibe QR code" className="membership-checkout-account-qr" />
                                ) : null}
                            </>
                        ) : (
                            <>
                                <h3 className="membership-checkout-account-title">Sign in to your account first.</h3>
                                <p className="membership-checkout-account-copy">
                                    Membership is a separate paid lane, but it must attach to the verified user account that owns your unique ID and QR.
                                </p>
                                {!loading ? (
                                    <div className="membership-checkout-account-actions">
                                        <button type="button" className="membership-checkout-account-button" onClick={() => navigate('/login')}>
                                            Sign In
                                        </button>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                    <div className="membership-checkout-summary">
                    <div className="membership-checkout-summary-row">
                        <dt>First Name</dt>
                        <dd>{resolvedFirstName}</dd> 
                    </div>
                    <div className="membership-checkout-summary-row">
                        <dt>Last Name</dt>
                        <dd>{resolvedLastName}</dd>
                    </div>
                    <div className="membership-checkout-summary-row">
                        <dt>Email</dt>
                        <dd>{resolvedEmail}</dd> 
                    </div>
                    <div className="membership-checkout-summary-row">
                        <dt>Date of Birth</dt>
                        <dd>{formData.dob}</dd> 
                    </div>
                    <div className="membership-checkout-summary-row">
                        <dt>Photo</dt>
                        <dd>
                            <img src={formData.photo} alt="Member" className="membership-checkout-photo" /> 
                        </dd>
                    </div>

                        <div className="membership-checkout-signature">
                            <label>Signature</label>
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                canvasProps={{ className: 'membership-checkout-signature-canvas' }}
                            />
                            <button onClick={clearSignature} className="membership-checkout-clear-signature">Clear</button>
                        </div>
                    </div>
                    
                    <form onSubmit={handlePaymentSubmission} className="membership-checkout-form">
                        <button type="submit" className="membership-checkout-submit-button" disabled={isLoading || !canStartCheckout || !priceDetails}>
                            {isLoading ? 'Processing...' : 'Confirm Membership & Pay Now'}
                        </button>
                    </form>
                    <button onClick={prevStep} className="membership-checkout-back-button">Back</button>
                </div>
                {clientSecret && (
                    <div className="membership-checkout-payment">
                        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                            <EmbeddedCheckout sessionId={sessionId} />
                        </EmbeddedCheckoutProvider>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MembershipCheckout;

